/**
 * AuditLogPage — Administration › Audit-Log (Story UI-U6 AC9).
 *
 * Filterable table over GET /audit (Zeitpunkt, Akteur = actor_kind + user_id,
 * Aktion, Objekt = entity_type/entity_id, Schweregrad, ID mono) with entity_type /
 * action / actor_kind / severity / from / to controls and keyset "Mehr laden" via
 * page.next_cursor.
 * The prototype's Lock immutability notice is kept; its IP column is dropped
 * (not in AuditEntry — honest omission). before/after/context stay unrendered
 * (opaque, possibly-PII JSON — never surfaced in the table).
 */

import { useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import type { AuditEntry } from '@brokly/shared';
import { Badge } from '../../../components/ui/Badge';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useAuditLog } from '../hooks';
import { Button, LoadingState, ErrorState, EmptyState, TextInput } from '../components';

const meta = ADMIN_PAGE_META['audit-log'];

const ACTOR_LABELS: Record<string, string> = {
  human: 'Person',
  system: 'System',
  ai: 'KI',
};

/** audit_log.severity labels. 'high' is badged; 'normal' stays quiet text. */
const SEVERITY_LABELS: Record<string, string> = {
  normal: 'Normal',
  high: 'Hoch',
};

function fmtTs(d: string): string {
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('de-CH');
}

interface AuditFilters {
  entity_type?: string;
  action?: string;
  actor_kind?: string;
  severity?: string;
  from?: string;
  to?: string;
}

export function AuditLogPage() {
  // Draft filter form → applied filters (a new query key resets pagination).
  const [draft, setDraft] = useState<AuditFilters>({});
  const [applied, setApplied] = useState<AuditFilters>({});
  const cleanApplied = useMemo(
    () =>
      Object.fromEntries(Object.entries(applied).filter(([, v]) => v)) as Record<string, string>,
    [applied],
  );
  const { rows, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useAuditLog(cleanApplied);

  const setField = (k: keyof AuditFilters, v: string) =>
    setDraft((d) => ({ ...d, [k]: v || undefined }));

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      {/* Lock notice — immutability (kept from the prototype). */}
      <div className="mb-4 flex items-start gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        <Lock className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Unveränderbares Protokoll: Einträge können weder bearbeitet noch gelöscht werden
          (WORM).
        </span>
      </div>

      {/* Filters */}
      <div className="mb-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <TextInput
          placeholder="Objekttyp"
          aria-label="Objekttyp"
          value={draft.entity_type ?? ''}
          onChange={(e) => setField('entity_type', e.target.value)}
        />
        <TextInput
          placeholder="Aktion"
          aria-label="Aktion"
          value={draft.action ?? ''}
          onChange={(e) => setField('action', e.target.value)}
        />
        <select
          aria-label="Akteur"
          value={draft.actor_kind ?? ''}
          onChange={(e) => setField('actor_kind', e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">Akteur (alle)</option>
          <option value="human">Person</option>
          <option value="system">System</option>
          <option value="ai">KI</option>
        </select>
        <select
          aria-label="Schweregrad"
          value={draft.severity ?? ''}
          onChange={(e) => setField('severity', e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">Schweregrad (alle)</option>
          <option value="normal">Normal</option>
          <option value="high">Hoch</option>
        </select>
        <TextInput
          type="date"
          aria-label="Von"
          value={draft.from ?? ''}
          onChange={(e) => setField('from', e.target.value)}
        />
        <TextInput
          type="date"
          aria-label="Bis"
          value={draft.to ?? ''}
          onChange={(e) => setField('to', e.target.value)}
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Button size="sm" onClick={() => setApplied(draft)}>
          Filtern
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft({});
            setApplied({});
          }}
        >
          Zurücksetzen
        </Button>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : rows.length === 0 ? (
        <EmptyState label="Keine Audit-Einträge für diese Filter." />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left text-xs font-medium text-muted-foreground">
                  <th className="px-3 py-2">Zeitpunkt</th>
                  <th className="px-3 py-2">Akteur</th>
                  <th className="px-3 py-2">Aktion</th>
                  <th className="px-3 py-2">Objekt</th>
                  <th className="px-3 py-2">Schweregrad</th>
                  <th className="px-3 py-2">ID</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e: AuditEntry) => (
                  <tr key={e.id} className="border-b border-border last:border-0">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                      {fmtTs(e.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span>{ACTOR_LABELS[e.actor_kind] ?? e.actor_kind}</span>
                      {e.user_id != null && (
                        <span className="ml-1.5 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {e.user_id.slice(0, 8)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium">{e.action}</td>
                    <td className="px-3 py-2">
                      <span>{e.entity_type}</span>
                      {e.entity_id != null && (
                        <span className="ml-1.5 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {e.entity_id.slice(0, 8)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e.severity === 'high' ? (
                        <Badge variant="warning">{SEVERITY_LABELS['high']}</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {SEVERITY_LABELS[e.severity] ?? e.severity}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-muted-foreground">
                      {e.id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? 'Wird geladen …' : 'Mehr laden'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
